import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  CheckCircle2,
  Clock,
  ArrowLeft,
  ShieldCheck,
  Smartphone,
  ChevronRight,
  CreditCard,
  Building2,
  Lock
} from 'lucide-react';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import subscriptionData from '../../../../../data/subscription/subscriptionData.json';

const downgradeOptions = subscriptionData.modalReasonOptions?.downgrade || [];
const upgradeOptions = subscriptionData.modalReasonOptions?.upgrade || subscriptionData.modalReasonOptions?.annualUpgrade || [];
const cycleSwitchOptions = subscriptionData.modalReasonOptions?.cycleSwitch || subscriptionData.modalReasonOptions?.switchMonthly || [];

const SubscriptionModals = ({
  showRequestModal,
  setShowRequestModal,
  selectedTargetPlan,
  setSelectedTargetPlan,
  targetInterval,
  requestType,
  requestReason,
  setRequestReason,
  requestComments,
  setRequestComments,
  isSubmittingRequest,
  requestSuccessMessage,
  setRequestSuccessMessage,
  handleSubmitSubscriptionRequest,
  showUpgradeModal,
  setShowUpgradeModal,
  selectedUpgradePlan,
  isUpgrading,
  handleConfirmUpgrade,
  showCheckoutPage,
  setShowCheckoutPage,
  triggerRazorpayMandate,
  subscription,
  cycleEndDate,
  formatDate,
  formatPrice
}) => {
  const isAnyModalOpen = Boolean(showRequestModal || showUpgradeModal || showCheckoutPage);

  useEffect(() => {
    if (isAnyModalOpen) {
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      const mainEl = document.querySelector('main');
      const originalMainOverflow = mainEl ? mainEl.style.overflow : '';

      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      if (mainEl) {
        mainEl.style.overflow = 'hidden';
      }

      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
        if (mainEl) {
          mainEl.style.overflow = originalMainOverflow;
        }
      };
    }
  }, [isAnyModalOpen]);

  if (!isAnyModalOpen || typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/* 1. Subscription Request Modal */}
      {showRequestModal && selectedTargetPlan && (
        <div
          className="fixed inset-0 z-[99999] bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl animate-modal-entry border border-slate-100">
            {requestSuccessMessage ? (
              <div className="p-7 text-center space-y-4">
                <div className="w-16 h-16 bg-teal-50 text-[#067978] rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 size={36} />
                </div>
                <h2 className="font-bold text-lg text-slate-900">Request Received</h2>
                <p className="text-xs text-slate-600 leading-relaxed max-w-sm mx-auto">
                  {requestSuccessMessage}
                </p>
                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 text-xs text-slate-600 text-left space-y-2">
                  <div className="flex gap-2 items-start font-semibold text-slate-800">
                    <Clock size={16} className="text-[#067978] shrink-0 mt-0.5" />
                    <span>What happens next?</span>
                  </div>
                  <p className="text-[11px] leading-relaxed text-slate-500 pl-6">
                    Our relationship team will coordinate with you in 24–48 hours to confirm the change and assist with account setup.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowRequestModal(false);
                    setSelectedTargetPlan(null);
                    setRequestSuccessMessage(null);
                  }}
                  className="w-full py-2.5 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl shadow-md shadow-[#067978]/20 transition-all cursor-pointer"
                >
                  Got It
                </button>
              </div>
            ) : (
              <>
                <div className="p-6 pb-4 flex justify-between items-start border-b border-slate-100">
                  <div>
                    <h2 className="font-bold text-base text-slate-900">
                      {requestType === 'switch_billing_cycle'
                        ? 'Switch to monthly billing'
                        : requestType === 'upgrade'
                        ? `Upgrade to ${selectedTargetPlan.name} (Annual)`
                        : `Request downgrade to ${selectedTargetPlan.name}`}
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">Your request will be processed by our team.</p>
                  </div>
                  <button onClick={() => setShowRequestModal(false)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                    <X size={18} />
                  </button>
                </div>
                <div className="p-6 pt-4 space-y-4">
                  <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-normal">Target plan</span>
                      <b className="font-semibold text-slate-800">{selectedTargetPlan.name} Tier</b>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-normal">Target cycle</span>
                      <b className="font-semibold text-slate-800 capitalize">{targetInterval}ly</b>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-normal">Rate</span>
                      <b className="font-semibold text-[#067978]">
                        {targetInterval === 'year'
                          ? `${formatPrice(selectedTargetPlan.yearly_price || Math.round(selectedTargetPlan.monthly_price * 12 * 0.8))} / year`
                          : `${formatPrice(selectedTargetPlan.monthly_price)} / month`
                        } + 18% GST
                      </b>
                    </div>
                  </div>

                  <div>
                    <CustomSelect
                      label="Reason for request"
                      value={requestReason}
                      onChange={(val) => setRequestReason(val)}
                      options={
                        requestType === 'downgrade'
                          ? downgradeOptions
                          : requestType === 'upgrade'
                            ? upgradeOptions
                            : cycleSwitchOptions
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Comments (optional)</label>
                    <textarea
                      rows="2"
                      value={requestComments}
                      onChange={(e) => setRequestComments(e.target.value)}
                      placeholder="Any specific note for the team..."
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-[#067978]/20 focus:border-[#067978]"
                    />
                  </div>

                  <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
                    <button
                      onClick={() => setShowRequestModal(false)}
                      className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-600 rounded-xl cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitSubscriptionRequest}
                      disabled={isSubmittingRequest}
                      className="px-4 py-2 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-[#067978]/20 flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
                    >
                      {isSubmittingRequest ? (
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span>Submit Request</span>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 2. Mid-Cycle Upgrade Confirmation Modal */}
      {showUpgradeModal && selectedUpgradePlan && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden animate-modal-entry">
            <div className="p-6 pb-4 flex justify-between items-center border-b border-slate-100">
              <div>
                <h2 className="font-bold text-base text-slate-900">
                  Upgrade to {selectedUpgradePlan.name} Tier
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Instant mid-cycle plan upgrade</p>
              </div>
              <button onClick={() => setShowUpgradeModal(false)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X size={18} />
              </button>
            </div>

            {(() => {
              const rawDays = cycleEndDate ? Math.max(0, Math.ceil((new Date(cycleEndDate) - new Date()) / (1000 * 60 * 60 * 24))) : 30;
              const daysRemaining = Math.min(30, Math.max(1, rawDays));

              return (
                <div className="p-6 pt-4 space-y-4">
                  {/* Plan Comparison */}
                  <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 flex justify-between items-center text-xs">
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 block">Current Plan</span>
                      <b className="text-xs text-slate-800 font-bold">{subscription?.plan_id?.name || 'Starter'} Tier</b>
                      <span className="text-[11px] text-slate-500 block mt-0.5">Ends {formatDate(cycleEndDate)} · <b className="text-teal-700">{daysRemaining} days left</b></span>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-600 block">Upgrading To</span>
                      <b className="text-xs text-[#067978] font-bold">{selectedUpgradePlan.name} Tier</b>
                      <span className="text-[11px] text-teal-700 block mt-0.5">{selectedUpgradePlan.name} Tier Features</span>
                    </div>
                  </div>

                  {/* Promotional Offer Banner */}
                  <div className="rounded-2xl p-4 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border border-emerald-300/80 shadow-2xs space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2.5 py-0.5 rounded-full border border-emerald-300">
                        Special Upgrade Perk
                      </span>
                      <span className="text-[10px] font-semibold text-emerald-700">100% Free Remainder</span>
                    </div>
                    <p className="text-xs text-slate-800 font-medium leading-relaxed">
                      Start using <span className="text-[#067978] font-bold">{selectedUpgradePlan.name} Tier</span> features today for the remaining <span className="font-bold text-slate-900">{daysRemaining} days</span> completely <span className="text-emerald-700 font-bold">FREE</span> (included in your current plan).
                    </p>
                    <p className="text-[11px] text-slate-600">
                      Regular renewal rate of <b>{formatPrice(selectedUpgradePlan.monthly_price)} / 30 days (+18% GST)</b> will apply starting <b>{formatDate(cycleEndDate)}</b>.
                    </p>
                  </div>

                  {/* Summary Breakdown */}
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/90 text-xs space-y-2.5">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500 font-medium">Immediate charge today:</span>
                      <b className="text-emerald-700 font-bold bg-emerald-50 px-2.5 py-0.5 rounded border border-emerald-200">
                        ₹5.00 (Refundable bank verification)
                      </b>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700 font-bold">First automated debit date:</span>
                      <b className="text-[#067978] font-black text-xs bg-teal-50 px-2.5 py-1 rounded-lg border border-teal-200 shadow-2xs">
                        {formatDate(cycleEndDate)}
                      </b>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500 font-medium">Next cycle charge:</span>
                      <b className="text-slate-900 font-bold">{formatPrice(selectedUpgradePlan.monthly_price)} / 30 days (+18% GST)</b>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
                    <button
                      onClick={() => setShowUpgradeModal(false)}
                      className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-600 rounded-xl cursor-pointer"
                    >
                      Keep Current Plan
                    </button>
                    <button
                      onClick={handleConfirmUpgrade}
                      disabled={isUpgrading}
                      className="px-5 py-2.5 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-[#067978]/20 flex items-center justify-center cursor-pointer disabled:opacity-60"
                    >
                      {isUpgrading ? (
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span>Authorize Mandate &amp; Upgrade to {selectedUpgradePlan.name}</span>
                      )}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* 3. Dedicated Subscription Mandate Checkout Modal */}
      {showCheckoutPage && selectedUpgradePlan && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md overflow-y-auto"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="bg-[#FAFAFA] rounded-3xl max-w-md w-full shadow-2xl border border-slate-200 overflow-hidden my-auto animate-modal-entry">
            {/* Top Header Bar */}
            <div className="p-5 pb-3 flex items-center justify-between border-b border-slate-200/60 bg-white">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowCheckoutPage(false)}
                  className="w-9 h-9 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-100 transition-all cursor-pointer shadow-xs"
                >
                  <ArrowLeft size={18} />
                </button>
                <div>
                  <h3 className="font-extrabold text-sm text-slate-900">Subscription Mandate Checkout</h3>
                  <span className="text-[11px] text-slate-500">Secure e-Mandate Authorization</span>
                </div>
              </div>
              <div className="flex items-center gap-1 bg-teal-50 text-[#067978] px-2.5 py-1 rounded-full text-[11px] font-bold border border-teal-200/80">
                <ShieldCheck size={13} />
                <span>Secure</span>
              </div>
            </div>

            <div className="p-5 pt-4 space-y-4">
              {/* Price & Mandate Summary Card */}
              {(() => {
                const formattedDebitDate = cycleEndDate ? formatDate(cycleEndDate) : 'cycle end date';

                return (
                  <div className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-2xs space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Mandate Verification Fee</span>
                        <div className="flex items-baseline gap-2 mt-0.5">
                          <span className="text-3xl font-black text-slate-900">₹5.00</span>
                          <span className="text-[10px] text-emerald-800 font-extrabold bg-emerald-100/90 px-2 py-0.5 rounded-full border border-emerald-300">100% Refundable</span>
                        </div>
                      </div>
                      <div className="w-12 h-12 rounded-2xl bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 border border-teal-100 font-black text-lg shadow-2xs">
                        {selectedUpgradePlan.name.charAt(0)}
                      </div>
                    </div>

                    <div className="border-t border-dashed border-slate-200 pt-3 space-y-2 text-xs">
                      <div className="flex justify-between items-center text-slate-600">
                        <span>Target Plan:</span>
                        <b className="text-slate-900 font-bold">{selectedUpgradePlan.name} Tier</b>
                      </div>
                      <div className="flex justify-between items-center text-slate-600">
                        <span>Next cycle recurring rate:</span>
                        <b className="text-slate-900 font-bold">{formatPrice(selectedUpgradePlan.monthly_price)} / 30 days (+18% GST)</b>
                      </div>
                      <div className="flex justify-between items-center p-2.5 rounded-xl bg-teal-50/80 border border-teal-200/80">
                        <span className="text-teal-900 font-bold">First automated debit date:</span>
                        <b className="text-[#067978] font-extrabold text-xs bg-white px-2.5 py-1 rounded-md border border-teal-200 shadow-2xs">
                          {formattedDebitDate}
                        </b>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Mandate Payment Options */}
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 block mb-2.5 px-1">
                  PAYMENT OPTIONS FOR MANDATE
                </span>
                <div className="space-y-2.5">
                  {/* UPI Option */}
                  <div
                    onClick={() => triggerRazorpayMandate('upi')}
                    className="bg-white hover:bg-teal-50/50 p-4 rounded-2xl border border-slate-200/90 flex items-center justify-between cursor-pointer transition-all shadow-2xs group"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-xl bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform border border-teal-100">
                        <Smartphone size={20} />
                      </div>
                      <div>
                        <h5 className="font-bold text-sm text-slate-900">UPI Autopay / Mandate</h5>
                        <p className="text-xs text-slate-500">Google Pay, PhonePe, Paytm, BHIM</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">Recommended</span>
                      <ChevronRight size={18} className="text-slate-400 group-hover:text-[#067978] group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </div>

                  {/* Debit / Credit Cards Option */}
                  <div
                    onClick={() => triggerRazorpayMandate('card')}
                    className="bg-white hover:bg-teal-50/50 p-4 rounded-2xl border border-slate-200/90 flex items-center justify-between cursor-pointer transition-all shadow-2xs group"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-xl bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform border border-teal-100">
                        <CreditCard size={20} />
                      </div>
                      <div>
                        <h5 className="font-bold text-sm text-slate-900">Debit / Credit Card Mandate</h5>
                        <p className="text-xs text-slate-500">Visa, Mastercard, RuPay e-Mandate</p>
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-slate-400 group-hover:text-[#067978] group-hover:translate-x-0.5 transition-all" />
                  </div>

                  {/* Netbanking Option */}
                  <div
                    onClick={() => triggerRazorpayMandate('netbanking')}
                    className="bg-white hover:bg-teal-50/50 p-4 rounded-2xl border border-slate-200/90 flex items-center justify-between cursor-pointer transition-all shadow-2xs group"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-xl bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform border border-teal-100">
                        <Building2 size={20} />
                      </div>
                      <div>
                        <h5 className="font-bold text-sm text-slate-900">Netbanking e-Mandate</h5>
                        <p className="text-xs text-slate-500">HDFC, ICICI, SBI, Axis &amp; Indian banks</p>
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-slate-400 group-hover:text-[#067978] group-hover:translate-x-0.5 transition-all" />
                  </div>
                </div>
              </div>

              {/* Security & Trust Footer */}
              <div className="p-3.5 rounded-2xl bg-teal-50/60 border border-teal-100 flex items-center justify-between text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <Lock size={15} className="text-[#067978]" />
                  <span>Encrypted &amp; Secured by <b>Razorpay</b></span>
                </div>
                <span className="text-[10px] font-bold text-teal-800 bg-white px-2 py-0.5 rounded border border-teal-200">RBI Mandate Compliant</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default SubscriptionModals;
